import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast, Toaster } from "sonner";
import { Building2, Save, Sparkles } from "lucide-react";
import AddressAutocomplete from "@/components/AddressAutocomplete";

const TYPES = ["Domaine", "Château", "Caviste", "Distillerie", "Brasserie"];

export default function MyProducer() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "", type: "Domaine", region: "", country: "France", description: "",
    image_url: "", website: "", address: "", phone: "", email: "",
    appellations: "", cepages: "",
  });
  const [existing, setExisting] = useState(null);
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);

  useEffect(() => {
    if (!user) { navigate("/login?next=/producers/mine"); return; }
    api.get("/producers-mine/me").then((r) => {
      if (r.data) {
        setExisting(r.data);
        setForm({
          ...r.data,
          appellations: (r.data.appellations || []).join(", "),
          cepages: (r.data.cepages || []).join(", "),
        });
      }
    });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...form,
        appellations: form.appellations ? form.appellations.split(",").map((s) => s.trim()).filter(Boolean) : [],
        cepages: form.cepages ? form.cepages.split(",").map((s) => s.trim()).filter(Boolean) : [],
        lat: form.lat ?? null,
        lng: form.lng ?? null,
      };
      if (existing) {
        await api.put(`/producers/${existing.id}`, payload);
        toast.success("Fiche mise à jour");
      } else {
        const { data } = await api.post("/producers", payload);
        toast.success("Fiche publiée");
        navigate(`/producers/${data.slug}`);
        return;
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  const aiFill = async () => {
    if (!form.name.trim()) { toast.error("Indiquez d'abord le nom"); return; }
    setAiBusy(true);
    try {
      const { data } = await api.post("/producers/lookup", { name: form.name, location: form.region });
      setForm((f) => ({
        ...f,
        description: data.summary || f.description,
        image_url: data.photo_url || f.image_url,
        website: data.website || f.website,
        address: data.address || f.address,
        phone: data.phone || f.phone,
        appellations: (data.appellations || []).join(", ") || f.appellations,
        cepages: (data.cepages || []).join(", ") || f.cepages,
        lat: data.location?.lat ?? f.lat,
        lng: data.location?.lng ?? f.lng,
      }));
      toast.success("Pré-rempli par geoVINUM AI");
    } catch {
      toast.error("Impossible de pré-remplir");
    } finally { setAiBusy(false); }
  };

  if (!user) return null;

  return (
    <div className="max-w-3xl mx-auto px-5 md:px-10 py-12 md:py-16">
      <Toaster theme="light" />
      <span className="chip-pink">Espace Pro</span>
      <h1 className="font-display text-4xl text-[#1F3A5F] mt-4 leading-[1.05]">{existing ? "Modifier ma fiche" : "Créer ma fiche gratuite"}</h1>
      <p className="text-[#4A5568] mt-3">1 fiche entreprise gratuite par compte. Modifiable à volonté.</p>

      <form onSubmit={submit} className="mt-8 grid md:grid-cols-2 gap-5 p-6 md:p-8 rounded-2xl border border-[#1F3A5F]/10 bg-white shadow-sm">
        <Field label="Nom *"><input required className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Domaine Raveneau" /></Field>
        <Field label="Type">
          <select className={inputCls} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
            {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </Field>
        <div className="md:col-span-2 -mt-2">
          <button type="button" onClick={aiFill} disabled={aiBusy} className="inline-flex items-center gap-2 text-xs text-[#E5276A] font-bold hover:underline disabled:opacity-50">
            <Sparkles className="w-3.5 h-3.5" /> {aiBusy ? "Recherche…" : "Pré-remplir par geoVINUM AI"}
          </button>
        </div>
        <Field label="Région"><input className={inputCls} value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} placeholder="Bourgogne" /></Field>
        <Field label="Pays"><input className={inputCls} value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} /></Field>
        <Field label="Description" full><textarea rows={5} className={inputCls} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
        <Field label="Image (URL)" full><input className={inputCls} value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="https://…" /></Field>
        <Field label="Site web"><input className={inputCls} value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} /></Field>
        <Field label="Téléphone"><input className={inputCls} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
        <Field label="Email contact"><input type="email" className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
        <Field label="Adresse" full>
          <AddressAutocomplete
            value={form.address}
            onChange={(v) => setForm({ ...form, address: v })}
            onSelect={(place) => setForm((f) => ({
              ...f,
              address: place.address || place.name || f.address,
              region: f.region || place.region || place.city || "",
              country: f.country || place.country || "France",
              lat: place.lat ?? f.lat,
              lng: place.lng ?? f.lng,
              google_place_id: place.place_id ?? f.google_place_id,
              phone: f.phone || place.phone || "",
              website: f.website || place.website || "",
            }))}
            placeholder="Tapez l'adresse du domaine…"
            testId="producer-address-ac"
          />
        </Field>
        <Field label="Appellations (séparées par virgule)" full><input className={inputCls} value={form.appellations} onChange={(e) => setForm({ ...form, appellations: e.target.value })} placeholder="Chablis, Petit Chablis" /></Field>
        <Field label="Cépages (séparés par virgule)" full><input className={inputCls} value={form.cepages} onChange={(e) => setForm({ ...form, cepages: e.target.value })} placeholder="Chardonnay" /></Field>
        <div className="md:col-span-2">
          <button disabled={busy} className="w-full btn-primary disabled:opacity-50"><Save className="w-4 h-4" /> {existing ? "Mettre à jour" : "Publier ma fiche"}</button>
        </div>
      </form>
    </div>
  );
}

const inputCls = "w-full bg-white border border-[#1F3A5F]/15 focus:border-[#E5276A] focus:ring-2 focus:ring-[#E5276A]/10 outline-none rounded-lg px-4 py-2.5 text-sm text-[#1F3A5F] placeholder:text-[#8B95A1]";
const Field = ({ label, children, full }) => (
  <label className={`flex flex-col gap-1.5 ${full ? "md:col-span-2" : ""}`}>
    <span className="text-[11px] uppercase tracking-[0.18em] text-[#E5276A] font-bold">{label}</span>
    {children}
  </label>
);
